import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Minus, ShoppingCart, Check, MapPin, Phone, Truck, ChevronLeft, ChevronRight } from 'lucide-react';

function useCatLabels() {
  const { t } = useTranslation();
  return { main: t('conv.main'), appetizer: t('conv.appetizer'), dessert: t('conv.dessert'), beverage: t('conv.beverage'), all: t('conv.all') };
}

export function MenuCard({ data, color = '#D97706', onAddToCart, compact = false }) {
  const [activeCat, setActiveCat] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const catLabels = useCatLabels();
  const items = data?.items || [];
  const cats = [...new Set(items.map(i => i.category).filter(Boolean))];
  const filtered = activeCat === 'all' ? items : items.filter(i => i.category === activeCat);
  const INITIAL_SHOW = 4;
  const visible = showAll ? filtered : filtered.slice(0, INITIAL_SHOW);
  const hasMore = filtered.length > INITIAL_SHOW && !showAll;

  return (
    <div className="mt-2" data-testid="rich-menu-card">
      {cats.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2 custom-scrollbar">
          <TabBtn active={activeCat === 'all'} color={color} onClick={() => { setActiveCat('all'); setShowAll(false); }} label={catLabels.all} />
          {cats.map(cat => <TabBtn key={cat} active={activeCat === cat} color={color} onClick={() => { setActiveCat(cat); setShowAll(false); }} label={catLabels[cat] || cat} />)}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {visible.map((item, i) => (
          <ItemCard key={item.id || i} item={item} color={color} onAdd={onAddToCart} compact={compact} index={i} />
        ))}
      </div>
      {hasMore && (
        <button onClick={() => setShowAll(true)} data-testid="show-more-menu"
          className="w-full mt-2 py-2.5 rounded-xl text-xs font-bold transition-all"
          style={{ background: `${color}10`, border: `1px solid ${color}25`, color }}>
          {catLabels.all === 'الكل' ? `عرض المزيد (${filtered.length - INITIAL_SHOW}+)` : `Show more (${filtered.length - INITIAL_SHOW}+)`}
        </button>
      )}
    </div>
  );
}

function TabBtn({ active, color, onClick, label }) {
  return (
    <button onClick={onClick} className="px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all flex-shrink-0"
      style={{
        background: active ? `${color}15` : '#F5F5F4',
        border: `1px solid ${active ? `${color}40` : '#E7E5E4'}`,
        color: active ? color : '#78716C',
      }}>
      {label}
    </button>
  );
}

function ItemCard({ item, color, onAdd, compact, index }) {
  const { t } = useTranslation();
  const [added, setAdded] = useState(false);

  const handleAdd = (qty = 1) => {
    if (onAdd) onAdd(item, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <div className="rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: '#FFFFFF', border: '1px solid #F0EDEA',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        animationDelay: `${index * 0.05}s`
      }}>
      {item.image_url ? (
        <div className="relative overflow-hidden" style={{ height: compact ? 64 : 95 }}>
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height: compact ? 48 : 60, background: `linear-gradient(135deg, ${color}12, ${color}06)` }}>
          <span className="text-2xl opacity-40">🍽️</span>
        </div>
      )}
      <div className="p-2.5">
        <div className="text-[12px] font-bold truncate mb-0.5" style={{ color: '#1C1917', fontFamily: 'Cairo, sans-serif' }}>{item.name}</div>
        {!compact && item.description && (
          <div className="text-[9px] leading-relaxed mb-1.5 line-clamp-1" style={{ color: '#A8A29E' }}>{item.description}</div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-extrabold" style={{ color }}>{item.price} {t('conv.sar')}</span>
          {added ? (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full" style={{ background: '#D1FAE5', border: '1px solid #A7F3D0' }}>
              <Check className="w-3 h-3 text-emerald-600" />
              <span className="text-[10px] font-bold text-emerald-600">{t('conv.addedShort') || 'تمت'}</span>
            </div>
          ) : (
            <button onClick={() => handleAdd(1)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all"
              style={{ background: color, boxShadow: `0 2px 8px ${color}35` }}
              data-testid={`add-item-${item.id}`}>
              <Plus className="w-3 h-3 text-white" />
              <span className="text-[10px] font-bold text-white">{t('conv.addBtn') || 'أضف'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CartCard({ data, color = '#D97706' }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl p-3.5 mt-2" data-testid="rich-cart-card" style={{ background: '#FFFFFF', border: '1px solid #F0EDEA', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div className="flex items-center gap-2 text-[11px] font-bold mb-2.5" style={{ color: '#57534E' }}>
        <ShoppingCart className="w-3.5 h-3.5" style={{ color }} /> {t('conv.cartTitle')}
      </div>
      {(data?.items || []).map((ci, k) => (
        <div key={k} className="flex justify-between text-[11px] py-1" style={{ color: '#78716C' }}>
          <span>{ci.name} x{ci.quantity}</span><span className="font-semibold">{ci.line_total} {t('conv.sar')}</span>
        </div>
      ))}
      <div className="flex justify-between text-[10px] mt-2 pt-2" style={{ borderTop: '1px solid #F0EDEA', color: '#A8A29E' }}>
        <span>{t('conv.subtotal')}</span><span>{data?.subtotal} {t('conv.sar')}</span>
      </div>
      <div className="flex justify-between text-[10px]" style={{ color: '#A8A29E' }}>
        <span>{t('conv.deliveryLabel')}</span><span>{data?.delivery_fee} {t('conv.sar')}</span>
      </div>
      <div className="flex justify-between text-xs font-bold mt-2 pt-2" style={{ borderTop: '1px solid #F0EDEA', color: '#1C1917' }}>
        <span>{t('conv.total')}</span><span style={{ color }}>{data?.total} {t('conv.sar')}</span>
      </div>
    </div>
  );
}

export function BranchCard({ data, color = '#D97706' }) {
  return (
    <div className="space-y-1.5 mt-2" data-testid="rich-branch-card">
      {(data || []).map((b, k) => (
        <div key={k} className="rounded-xl p-3.5" style={{ background: '#FFFFFF', border: '1px solid #F0EDEA', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="text-xs font-bold mb-1.5" style={{ color: '#1C1917' }}>{b.name}</div>
          {b.address && <div className="flex items-center gap-2 text-[10px]" style={{ color: '#A8A29E' }}><MapPin className="w-3 h-3" style={{ color }} />{b.address}</div>}
          {b.phone && <div className="flex items-center gap-2 text-[10px] mt-1" style={{ color: '#A8A29E' }}><Phone className="w-3 h-3" style={{ color }} /><span dir="ltr">{b.phone}</span></div>}
        </div>
      ))}
    </div>
  );
}

export function DeliveryCard({ data, color = '#D97706' }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl p-3.5 mt-2 flex items-center gap-3" data-testid="rich-delivery-card" style={{ background: '#FFFFFF', border: '1px solid #F0EDEA', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}12` }}>
        <Truck className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <div className="text-xs font-bold" style={{ color: '#1C1917' }}>{t('conv.deliveryFee')} {data?.total_fee} {t('conv.sar')}</div>
        <div className="text-[10px] mt-0.5" style={{ color: '#A8A29E' }}>{t('conv.estimatedTime')} {data?.estimated_time}</div>
      </div>
    </div>
  );
}

export function OrderConfirmedCard({ data }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl p-5 mt-2 text-center" data-testid="rich-order-confirmed" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
      <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: '0 0 20px rgba(16,185,129,0.3)' }}>
        <Check className="w-6 h-6 text-white" />
      </div>
      <div className="text-sm font-bold text-emerald-700">{t('conv.orderConfirmed')}</div>
      <div className="text-[10px] text-emerald-600 mt-1">{t('conv.orderThankYou')}</div>
      {data?.tracking_url && (
        <a href={data.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="tracking-link"
          className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-full text-[11px] font-semibold text-emerald-300 transition-all hover:bg-emerald-500/20"
          style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <Truck className="w-3.5 h-3.5" /> {t('conv.trackOrder')}
        </a>
      )}
      {data?.consent_notice && (
        <div className="mt-3 pt-2 border-t border-emerald-500/10">
          <p className="text-[9px] text-zinc-500 leading-relaxed" data-testid="consent-notice">{data.consent_notice}</p>
        </div>
      )}
    </div>
  );
}

export function SpecialOffersCard({ data, color = '#D97706', onAddToCart }) {
  const { t } = useTranslation();
  const offers = data?.offers || [];
  return (
    <div className="mt-2 space-y-2" data-testid="rich-offers-card">
      {offers.map((offer, i) => (
        <div key={i} className="rounded-xl overflow-hidden flex" style={{ background: '#FFFFFF', border: '1px solid #F0EDEA', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          {offer.image_url && (
            <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
              <img src={offer.image_url} alt={offer.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
            </div>
          )}
          <div className="flex-1 p-2.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>{offer.badge || t('conv.offer')}</span>
                {offer.discount_pct > 0 && <span className="text-[9px] font-bold text-red-500">-{offer.discount_pct}%</span>}
              </div>
              <div className="text-[11px] font-bold truncate" style={{ color: '#1C1917', fontFamily: 'Cairo, sans-serif' }}>{offer.name}</div>
            </div>
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-extrabold" style={{ color }}>{offer.offer_price} {t('conv.sar')}</span>
                {offer.original_price !== offer.offer_price && (
                  <span className="text-[9px] line-through" style={{ color: '#C1BAB4' }}>{offer.original_price} {t('conv.sar')}</span>
                )}
              </div>
              <button onClick={() => onAddToCart && onAddToCart({ id: offer.item_id, name: offer.name, price: offer.offer_price }, 1)}
                className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
                style={{ background: color, boxShadow: `0 2px 8px ${color}35` }}
                data-testid={`offer-add-${i}`}>
                <Plus className="w-3 h-3 text-white" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function PopularItemsCard({ data, color = '#D97706', onAddToCart }) {
  const { t } = useTranslation();
  const items = data?.items || [];
  return (
    <div className="mt-2 space-y-1.5" data-testid="rich-popular-card">
      {items.map((item, i) => (
        <div key={i} className="rounded-xl flex items-center gap-2.5 p-2.5" style={{ background: '#FFFFFF', border: '1px solid #F0EDEA', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[11px] font-black" style={{ background: i === 0 ? `${color}18` : '#F5F5F4', color: i === 0 ? color : '#A8A29E' }}>
            {item.popularity_rank || i + 1}
          </div>
          {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" referrerPolicy="no-referrer" loading="lazy" />}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold truncate" style={{ color: '#1C1917', fontFamily: 'Cairo, sans-serif' }}>{item.name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] font-extrabold" style={{ color }}>{item.price} {t('conv.sar')}</span>
              <span className="text-[8px]" style={{ color: '#C1BAB4' }}>{item.order_count} {t('conv.orderCount')}</span>
            </div>
          </div>
          <button onClick={() => onAddToCart && onAddToCart(item, 1)}
            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
            style={{ background: color, boxShadow: `0 2px 8px ${color}35` }}
            data-testid={`popular-add-${i}`}>
            <Plus className="w-3 h-3 text-white" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function ReviewsCard({ data, color = '#D97706' }) {
  const reviews = data?.reviews || [];
  const avgRating = data?.avg_rating || 0;
  return (
    <div className="mt-2" data-testid="rich-reviews-card">
      {avgRating > 0 && (
        <div className="flex items-center gap-2 mb-2 px-1">
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map(s => (
              <span key={s} className="text-xs" style={{ color: s <= Math.round(avgRating) ? '#FBBF24' : '#E7E5E4' }}>&#9733;</span>
            ))}
          </div>
          <span className="text-[10px] font-bold" style={{ color: '#78716C' }}>{avgRating}/5</span>
        </div>
      )}
      <div className="space-y-1.5">
        {reviews.slice(0, 3).map((review, i) => (
          <div key={i} className="rounded-xl p-3" style={{ background: '#FFFFFF', border: '1px solid #F0EDEA', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold" style={{ color: '#57534E' }}>{review.customer_name}</span>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <span key={s} className="text-[8px]" style={{ color: s <= (review.rating || 5) ? '#FBBF24' : '#E7E5E4' }}>&#9733;</span>
                ))}
              </div>
            </div>
            <p className="text-[10px] leading-relaxed" style={{ color: '#A8A29E' }}>{review.comment}</p>
            {review.item_name && <span className="text-[8px] mt-1 inline-block px-1.5 py-0.5 rounded-full" style={{ background: `${color}10`, color: `${color}CC` }}>{review.item_name}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CarouselCard({ data, color = '#D97706', onAddToCart }) {
  const { t } = useTranslation();
  const catLabels = useCatLabels();
  const slides = data?.slides || [];
  const scrollRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [addedId, setAddedId] = useState(null);

  const slideWidth = 220;
  const gap = 12;

  const scrollToIndex = (idx) => {
    if (!scrollRef.current) return;
    const clamped = Math.max(0, Math.min(idx, slides.length - 1));
    scrollRef.current.scrollTo({ left: clamped * (slideWidth + gap), behavior: 'smooth' });
    setActiveIdx(clamped);
  };

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const idx = Math.round(scrollRef.current.scrollLeft / (slideWidth + gap));
    setActiveIdx(Math.max(0, Math.min(idx, slides.length - 1)));
  };

  const handleAdd = (slide) => {
    if (onAddToCart) onAddToCart({ id: slide.id, name: slide.name, price: slide.has_offer && slide.offer_price ? slide.offer_price : slide.price }, 1);
    setAddedId(slide.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  if (!slides.length) return null;

  return (
    <div className="mt-3" data-testid="rich-carousel-card">
      <div className="relative">
        {slides.length > 1 && (
          <>
            <button onClick={() => scrollToIndex(activeIdx - 1)} data-testid="carousel-prev"
              className="absolute top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all"
              style={{ right: -4, background: 'rgba(0,0,0,0.6)', border: `1px solid ${color}40`, opacity: activeIdx < slides.length - 1 ? 1 : 0.3 }}>
              <ChevronRight className="w-4 h-4" style={{ color }} />
            </button>
            <button onClick={() => scrollToIndex(activeIdx + 1)} data-testid="carousel-next"
              className="absolute top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all"
              style={{ left: -4, background: 'rgba(0,0,0,0.6)', border: `1px solid ${color}40`, opacity: activeIdx > 0 ? 1 : 0.3 }}>
              <ChevronLeft className="w-4 h-4" style={{ color }} />
            </button>
          </>
        )}

        <div ref={scrollRef} onScroll={handleScroll} className="custom-scrollbar"
          style={{ display: 'flex', gap, overflowX: 'auto', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', paddingBottom: 8 }}>
          {slides.map((slide, i) => (
            <div key={slide.id || i} data-testid={`carousel-slide-${i}`}
              className="flex-shrink-0 rounded-2xl overflow-hidden transition-all duration-300"
              style={{
                width: slideWidth, scrollSnapAlign: 'start',
                background: '#FFFFFF', border: slide.is_trending ? '1px solid rgba(239,68,68,0.25)' : '1px solid #F0EDEA',
                boxShadow: slide.is_trending ? '0 4px 20px rgba(239,68,68,0.1)' : (i === activeIdx ? `0 4px 20px ${color}15` : '0 2px 8px rgba(0,0,0,0.06)'),
                transform: i === activeIdx ? 'scale(1)' : 'scale(0.97)',
              }}>
              <div className="relative overflow-hidden" style={{ height: 140 }}>
                {slide.image_url ? (
                  <img src={slide.image_url} alt={slide.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${color}15, ${color}06)` }}>
                    <span className="text-3xl opacity-40">🍽️</span>
                  </div>
                )}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(transparent 55%, rgba(0,0,0,0.45))' }} />
                {(slide.badge || slide.is_trending) && (
                  <div className="absolute top-2.5 right-2.5 flex flex-col gap-1">
                    {slide.is_trending && (
                      <span className="text-[9px] font-bold px-2 py-1 rounded-full flex items-center gap-1"
                        style={{ background: 'rgba(239,68,68,0.85)', color: '#fff', animation: 'pulse 2s ease-in-out infinite' }}>
                        <span style={{ fontSize: 10 }}>🔥</span> {t('conv.trendingNow')}
                        {slide.recent_orders > 0 && <span className="text-[8px] opacity-75">({slide.recent_orders})</span>}
                      </span>
                    )}
                    {slide.badge && !slide.is_trending && (
                      <span className="text-[9px] font-bold px-2 py-1 rounded-full"
                        style={{ background: slide.has_offer ? color : 'rgba(0,0,0,0.55)', color: '#fff' }}>
                        {slide.badge}
                      </span>
                    )}
                  </div>
                )}
                {slide.has_offer && slide.discount_pct > 0 && (
                  <div className="absolute top-2.5 left-2.5">
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded-md" style={{ background: '#EF4444', color: '#fff' }}>
                      -{slide.discount_pct}%
                    </span>
                  </div>
                )}
                {slide.category && (
                  <div className="absolute bottom-2.5 right-2.5">
                    <span className="text-[8px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.85)' }}>
                      {catLabels[slide.category] || slide.category}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-[12px] font-bold mb-0.5 truncate" style={{ color: '#1C1917', fontFamily: 'Cairo, sans-serif' }}>{slide.name}</div>
                {slide.description && (
                  <div className="text-[9px] leading-relaxed mb-2 line-clamp-2" style={{ color: '#A8A29E' }}>{slide.description}</div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    {slide.has_offer && slide.offer_price ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[13px] font-extrabold" style={{ color }}>{slide.offer_price} {t('conv.sar')}</span>
                        <span className="text-[9px] line-through" style={{ color: '#C1BAB4' }}>{slide.price} {t('conv.sar')}</span>
                      </div>
                    ) : (
                      <span className="text-[13px] font-extrabold" style={{ color }}>{slide.price} {t('conv.sar')}</span>
                    )}
                  </div>
                  {addedId === slide.id ? (
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: '#10B981' }}>
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                  ) : (
                    <button onClick={() => handleAdd(slide)} data-testid={`carousel-add-${i}`}
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
                      style={{ background: color, boxShadow: `0 2px 10px ${color}35` }}>
                      <Plus className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-2" data-testid="carousel-dots">
          {slides.map((_, i) => (
            <button key={i} onClick={() => scrollToIndex(i)}
              className="transition-all duration-300 rounded-full"
              style={{
                width: i === activeIdx ? 16 : 5, height: 5,
                background: i === activeIdx ? color : '#E7E5E4',
                boxShadow: i === activeIdx ? `0 0 6px ${color}50` : 'none',
              }} />
          ))}
        </div>
      )}
    </div>
  );
}

export function CheckoutRedirectCard({ data, color = '#D97706' }) {
  const { t } = useTranslation();
  return (
    <div data-testid="checkout-redirect-card" className="mt-3 card-enter" style={{
      background: '#FFFFFF', borderRadius: 16, border: `1px solid ${color}25`,
      padding: '20px', boxShadow: `0 4px 20px ${color}12`,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>💳</div>
        <h3 style={{ fontWeight: 800, fontSize: 16, color: '#1C1917', marginBottom: 6 }}>
          {t('conv.readyToPay') || 'جاهز للدفع!'}
        </h3>
        <div style={{ fontSize: 13, color: '#A8A29E', marginBottom: 12 }}>
          {data.item_count} {t('conv.itemsCount') || 'أصناف'} • {data.total} {t('conv.sar') || 'ر.س'}
        </div>
        <a href={data.checkout_url} data-testid="go-to-checkout-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 32px',
            borderRadius: 24, background: color,
            color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none',
            boxShadow: `0 4px 16px ${color}40`, transition: 'all 0.25s',
          }}>
          {t('conv.goToCheckout') || 'اذهب للدفع'} →
        </a>
        <div style={{ fontSize: 11, color: '#C1BAB4', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          🔒 {t('conv.securePayment') || 'دفع آمن ومشفر'}
        </div>
      </div>
    </div>
  );
}

export function ItemDetailCard({ data, color = '#D97706', onAddToCart }) {
  const { t } = useTranslation();
  const [added, setAdded] = useState(false);
  const item = data || {};
  return (
    <div data-testid="item-detail-card" className="mt-3 rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: `1px solid #F0EDEA`, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
      {item.image_url && (
        <div style={{ height: 160, position: 'relative', overflow: 'hidden' }}>
          <img src={item.image_url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" loading="lazy" />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 45%, rgba(0,0,0,0.6))' }} />
          <div style={{ position: 'absolute', bottom: 10, right: 12, left: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', fontFamily: 'Cairo, sans-serif', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>{item.name}</div>
          </div>
        </div>
      )}
      {!item.image_url && (
        <div style={{ padding: '14px 14px 0', fontWeight: 800, fontSize: 16, color: '#1C1917', fontFamily: 'Cairo, sans-serif' }}>{item.name}</div>
      )}
      <div style={{ padding: 14 }}>
        {item.description && <p style={{ fontSize: 12, color: '#78716C', lineHeight: 1.8, marginBottom: 10 }}>{item.description}</p>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 18, fontWeight: 800, color }}>{item.price} {t('conv.sar')}</span>
          {added ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, background: '#D1FAE5', border: '1px solid #A7F3D0' }}>
              <Check className="w-4 h-4 text-emerald-600" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>{t('conv.addedShort') || 'تمت'}</span>
            </div>
          ) : (
            <button onClick={() => { if (onAddToCart) onAddToCart(item, 1); setAdded(true); setTimeout(() => setAdded(false), 1500); }}
              data-testid={`detail-add-${item.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 20, background: color, border: 'none', cursor: 'pointer', boxShadow: `0 3px 12px ${color}35` }}>
              <Plus className="w-4 h-4 text-white" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{t('conv.addBtn') || 'أضف'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function RichCardRenderer({ card, color, onAddToCart, compact }) {
  switch (card.type) {
    case 'menu': return <MenuCard data={card.data} color={color} onAddToCart={onAddToCart} compact={compact} />;
    case 'cart': return <CartCard data={card.data} color={color} />;
    case 'branches': return <BranchCard data={card.data} color={color} />;
    case 'delivery': return <DeliveryCard data={card.data} color={color} />;
    case 'order_confirmed': return <OrderConfirmedCard data={card.data} />;
    case 'special_offers': return <SpecialOffersCard data={card.data} color={color} onAddToCart={onAddToCart} />;
    case 'popular_items': return <PopularItemsCard data={card.data} color={color} onAddToCart={onAddToCart} />;
    case 'reviews': return <ReviewsCard data={card.data} color={color} />;
    case 'carousel': return <CarouselCard data={card.data} color={color} onAddToCart={onAddToCart} />;
    case 'checkout_redirect': return <CheckoutRedirectCard data={card.data} color={color} />;
    case 'item_detail': return <ItemDetailCard data={card.data} color={color} onAddToCart={onAddToCart} />;
    default: return null;
  }
}
